/**
 * Week Comparison Chart
 * Grouped bar chart: completed services by day (Mon–Sun), this week vs last week.
 */

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { BarChart3, Info } from 'lucide-react'
import type { WeeklyServiceData } from '@/types/dashboard'
import { useLocalization } from '@/contexts/LocalizationContext'

// ==================== Colors ====================

const THIS_WEEK_COLOR = '#131375' // brand dark blue
const LAST_WEEK_COLOR = '#B0C4DE' // muted light blue

// ==================== Types ====================

interface WeekComparisonChartProps {
  thisWeekByDay: WeeklyServiceData[]
  lastWeekByDay: WeeklyServiceData[]
  title?: string
  loading?: boolean
}

interface ComparisonRow {
  day: string
  dayFull: string
  thisWeek: number
  lastWeek: number
}

// ==================== Component ====================

export function WeekComparisonChart({
  thisWeekByDay,
  lastWeekByDay,
  title,
  loading = false,
}: WeekComparisonChartProps) {
  const { t } = useLocalization()

  if (loading) {
    return (
      <Card className="h-full">
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-32 mt-1" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-64 w-full" />
        </CardContent>
      </Card>
    )
  }

  const rows: ComparisonRow[] = (thisWeekByDay || []).map((d, idx) => ({
    day: d.day,
    dayFull: d.dayFull,
    thisWeek: d.services,
    lastWeek: lastWeekByDay?.[idx]?.services ?? 0,
  }))

  const hasData = rows.some((r) => r.thisWeek > 0 || r.lastWeek > 0)

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-primary" />
          <CardTitle className="text-lg">{title || t('dashboard.thisWeekVsLastWeek')}</CardTitle>
        </div>
        <CardDescription className="mt-1">
          {t('dashboard.completedServicesByDay')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!hasData ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <Info className="h-12 w-12 text-muted-foreground/30 mb-4" />
            <h3 className="font-medium text-muted-foreground mb-2">
              {t('dashboard.noDataAvailable')}
            </h3>
          </div>
        ) : (
          <div className="h-64 min-h-[256px] min-w-0 w-full">
            <ResponsiveContainer width="100%" height={256}>
              <BarChart data={rows} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                <XAxis
                  dataKey="day"
                  tick={{ fill: '#6b7280', fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: '#6b7280', fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  cursor={{ fill: 'rgba(0, 0, 0, 0.04)' }}
                  labelFormatter={(_, payload) =>
                    (payload?.[0]?.payload as ComparisonRow | undefined)?.dayFull ?? ''
                  }
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar
                  dataKey="thisWeek"
                  name={t('dashboard.thisWeek')}
                  fill={THIS_WEEK_COLOR}
                  radius={[6, 6, 0, 0]}
                  maxBarSize={28}
                />
                <Bar
                  dataKey="lastWeek"
                  name={t('dashboard.lastWeek')}
                  fill={LAST_WEEK_COLOR}
                  radius={[6, 6, 0, 0]}
                  maxBarSize={28}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
